import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, CalendarCheck, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { useToast } from "@/hooks/use-toast";
import type { StayTabProps } from "./types";
import { useStayActivities, type ActivityRow as ActivityRowData } from "../activities/useStayActivities";
import { useToggleActivityCompletion } from "../activities/useActivityMutations";
import type { StayActivityRecord } from "../activities/activitiesModel";
import NewActivityDialog from "../activities/NewActivityDialog";
import { ActivityCard, ActivityTableRow } from "../activities/ActivityRow";
import { usePanelDay } from "../panel/usePanelDay";
import { FollowUpDialog } from "../stay/FollowUpDialog";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shift = (iso: string, days: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function ActivitiesTab({ show = "all" }: StayTabProps) {
  const { t, i18n } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const [day, setDay] = useState(todayIso);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<StayActivityRecord | null>(null);
  // Guarda o petId e deriva de `panelDay.data` a cada render — senão o modal
  // "Acompanhar" ficava preso no snapshot de quando foi aberto.
  const [followUpPetId, setFollowUpPetId] = useState<string | null>(null);
  const { data: rows = [], isLoading } = useStayActivities({ day, show });
  const panelDay = usePanelDay(day);
  const toggle = useToggleActivityCompletion();
  const followUpPet = followUpPetId ? (panelDay.data ?? []).find((pet) => pet.petId === followUpPetId) ?? null : null;

  const label = new Intl.DateTimeFormat(locale, { weekday: "short", day: "2-digit", month: "short" }).format(new Date(`${day}T12:00:00`));
  const isToday = day === todayIso();

  const onToggle = (activityId: string, done: boolean) => toggle.mutate({ activityId, day, done });

  const openRow = (row: ActivityRowData) => {
    if (row.targetType === "pet" && row.targetPetId) {
      const pet = (panelDay.data ?? []).find((p) => p.petId === row.targetPetId);
      if (!pet) {
        toast({ title: t("activities.reservationNotFound"), variant: "destructive" });
        return;
      }
      setFollowUpPetId(pet.petId);
      return;
    }
    setEditingActivity(row.record);
  };

  return (
    <div className="grid gap-4">
      {/* Navegador de dia + ações */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1">
          <Button variant="ghost" size="icon" aria-label={t("previous", { defaultValue: "Anterior" })} onClick={() => setDay(shift(day, -1))}><ChevronLeft size={18} /></Button>
          <div className="flex items-center gap-2 px-2">
            <Calendar size={16} className="text-primary" />
            <span className="min-w-[118px] text-center font-display text-[15px] font-bold text-foreground">{label}</span>
          </div>
          <Button variant="ghost" size="icon" aria-label={t("next", { defaultValue: "Próximo" })} onClick={() => setDay(shift(day, 1))}><ChevronRight size={18} /></Button>
        </div>
        {isToday && <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">{t("activities.today")}</span>}
        <div className="ml-auto flex items-center gap-4">
          <Button variant="outline" onClick={() => setModalOpen(true)}><Plus size={16} /> {t("activities.newActivity")}</Button>
        </div>
      </div>

      {/* Tabela / cards */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {/* header desktop */}
        <div className="hidden grid-cols-[60px_1.4fr_120px_1.6fr_130px_140px] gap-3 bg-muted/40 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
          <div>{t("activities.colHora")}</div><div>{t("activities.colAlvo")}</div><div>{t("activities.colTipo")}</div>
          <div>{t("activities.colDescricao")}</div><div>{t("activities.colResponsavel")}</div><div />
        </div>

        {isLoading ? (
          <div className="grid gap-3 p-5">
            <Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <CalendarCheck size={26} className="text-primary" />
            </div>
            <div className="space-y-1">
              <p className="font-display text-[15px] font-semibold text-foreground">{t("activities.empty")}</p>
              <p className="text-sm text-muted-foreground">{t("activities.emptyHint")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
              <Plus size={16} /> {t("activities.newActivity")}
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              {rows.map((row) => (
                <ActivityTableRow key={row.id} row={row} pending={toggle.isPending} onToggle={(done) => onToggle(row.id, done)} onOpen={() => openRow(row)} />
              ))}
            </div>
            <div className="md:hidden">
              {rows.map((row) => (
                <ActivityCard key={row.id} row={row} pending={toggle.isPending} onToggle={(done) => onToggle(row.id, done)} onOpen={() => openRow(row)} />
              ))}
            </div>
          </>
        )}
      </div>

      <NewActivityDialog open={modalOpen} onOpenChange={setModalOpen} defaultDate={day} />
      {editingActivity && (
        <NewActivityDialog
          open
          onOpenChange={(next) => { if (!next) setEditingActivity(null); }}
          defaultDate={editingActivity.scheduledDate ?? day}
          activity={editingActivity}
        />
      )}
      {followUpPet && (
        <FollowUpDialog
          pet={followUpPet}
          isoDate={day}
          open
          onOpenChange={(next) => { if (!next) setFollowUpPetId(null); }}
        />
      )}
    </div>
  );
}
